import React, { useEffect, useMemo, useState, createElement } from 'react';
import { subscribeToNewsletterSignups, type NewsletterSignup } from '../../../utils/firebase';
import { Search, Mail, Download, Copy, Check } from 'lucide-react';
function formatDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}
function formatSource(source: string): string {
  if (!source || source === 'unknown') return 'Unknown';
  return source.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
export function AdminNewsletter() {
  const [signups, setSignups] = useState<NewsletterSignup[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    const unsub = subscribeToNewsletterSignups(data => {
      setSignups(data);
      setLoading(false);
    });
    return unsub;
  }, []);
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return signups;
    return signups.filter(s => s.email.toLowerCase().includes(term) || formatSource(s.source).toLowerCase().includes(term));
  }, [signups, search]);
  const handleCopyAll = async () => {
    const emails = filtered.map(s => s.email).join(', ');
    try {
      await navigator.clipboard.writeText(emails);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — no-op */
    }
  };
  const handleExportCsv = () => {
    const header = 'email,source,signed_up\n';
    const rows = filtered.map(s => `${s.email},${(s.source || 'unknown').replace(/,/g, ' ')},${s.createdAt}`).join('\n');
    const blob = new Blob([header + rows], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'newsletter-signups.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  if (loading) {
    return <div className="p-8 flex justify-center" data-id="element-299" data-mp="3l5owinncyerz">
        <span className="w-6 h-6 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-300" data-mp="3q74ichxd0pbf" />
      </div>;
  }
  return <div className="p-8 max-w-7xl mx-auto" data-id="element-301" data-mp="2skxdd6x58uyj">
      <h1 className="text-2xl font-bold text-[#111111] mb-2" data-id="element-302" data-mp="2xhnttvcv1skh">
        Newsletter Signups
      </h1>
      <p className="text-sm text-[#5A5A5A] mb-8" data-id="element-303" data-mp="32gqoclv7sge7">
        Every email collected from newsletter forms across the site.
      </p>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10" data-id="element-304" data-mp="379bgliyh2lfm">
        <div className="bg-white p-6 rounded-xl border border-[#E5E5E5] shadow-sm" data-id="element-305" data-mp="29rten752jrx2">
          <p className="text-sm font-medium text-[#5A5A5A] mb-1" data-id="element-306" data-mp="2eqnzsfqidskc">
            Total Subscribers
          </p>
          <p className="text-3xl font-bold text-[#111111]" data-id="element-307" data-mp="vva3dug5la22">{signups.length}</p>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-xl shadow-sm overflow-hidden" data-id="element-308" data-mp="rvhpd27zqbpb">
        {/* Toolbar */}
        <div className="p-4 border-b border-[#E5E5E5] flex flex-col sm:flex-row gap-4 justify-between items-center bg-[#FAFAFA]" data-id="element-309" data-mp="mxzpe1byb6tg">
          <div className="relative w-full sm:w-96" data-id="element-310" data-mp="cxgyimlyyq21">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-311" data-mp="cza4s0so789i" />
            <input type="text" placeholder="Search emails or source..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-white border border-[#E5E5E5] rounded-md text-sm focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate" data-id="element-312" data-mp="85frpg7klrje" />
          </div>
          <div className="flex gap-3 w-full sm:w-auto" data-id="element-313" data-mp="3le2b0ddt819w">
            <button type="button" onClick={handleCopyAll} disabled={filtered.length === 0} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-[#E5E5E5] rounded-md text-sm font-medium text-[#111111] hover:border-tealslate hover:text-tealslate disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-314" data-mp="1panq306g4vpf">
              {copied ? <>
                  <Check className="w-4 h-4" data-id="element-315" data-mp="16il7hja8v5cj" /> Copied
                </> : <>
                  <Copy className="w-4 h-4" data-id="element-316" data-mp="1bgwivyg6tnwv" /> Copy emails
                </>}
            </button>
            <button type="button" onClick={handleExportCsv} disabled={filtered.length === 0} className="inline-flex items-center gap-2 px-3 py-2 bg-tealslate text-white rounded-md text-sm font-medium hover:bg-navyslate disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-317" data-mp="wo2zi1xsonmx">
              <Download className="w-4 h-4" data-id="element-318" data-mp="kc92jowz782s" /> Export CSV
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" data-id="element-319" data-mp="fabcobu2lf0v">
          <table className="w-full text-left border-collapse" data-id="element-320" data-mp="u1shwklhcjsw">
            <thead data-id="element-321" data-mp="u33qcxpvefqc">
              <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]" data-id="element-322" data-mp="pau2dn8bfpqp">
                <th className="px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-323" data-mp="p8hektfvml5q">
                  Email
                </th>
                <th className="px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-324" data-mp="z15556g9jsw0">
                  Source
                </th>
                <th className="px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-325" data-mp="1cy26w2xx6uro">
                  Signed Up
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E5E5]" data-id="element-326" data-mp="3errr0xe9gyx3">
              {filtered.length === 0 ? <tr data-id="element-327" data-mp="39u9detpatvir">
                  <td colSpan={3} className="px-6 py-12 text-center text-[#5A5A5A]" data-id="element-328" data-mp="39urkkldi1u5g">
                    {signups.length === 0 ? 'No newsletter signups yet.' : 'No signups match your search.'}
                  </td>
                </tr> : filtered.map(s => <tr key={s.email} className="hover:bg-[#FAFAFA] transition-colors" data-id="element-329" data-mp="1rmoynrrbr17p">
                    <td className="px-6 py-4" data-id="element-330" data-mp="21m7fhzbxu6vj">
                      <div className="flex items-center gap-2" data-id="element-331" data-mp="21kwa5v2ugr2b">
                        <Mail className="w-4 h-4 text-[#9A9A90] shrink-0" data-id="element-332" data-mp="26f8z2kn3aulg" />
                        <span className="text-sm font-medium text-[#111111] break-all" data-id="element-333" data-mp="2afsthgga3k3w">
                          {s.email}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-334" data-mp="2kes4h5oinru2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#F0F4F8] text-tealslate" data-id="element-335" data-mp="2kbdzrqsdr6jg">
                        {formatSource(s.source)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#5A5A5A]" data-id="element-336" data-mp="2kipsoi46wo5e">
                      {formatDate(s.createdAt)}
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}